import styles from './styles.module.scss';

import { useContext, useEffect, useState } from 'react';
import TrilogicIconLogin from '../../../../assets/images/icons/trilogic-icon-login.png';
import { useNavigate } from 'react-router-dom';
import { LoginBox } from '../../../../components/LoginBox';
import { MessageContext } from '../../../../providers/MessageProvider';
import { sendEmailVerification, verifyEmail } from './requestHandlers';
import {
    logout,
    saveSessionCredentials,
    verifyRoutePermissions
} from '../../../../globalFunctions';
import { User } from '../../../../types';
import { LoadingContext } from '../../../../providers/LoadingProvider';

export const EmailVerificationRoute = () => {
    const [userAuth, setUserAuth] = useState<User>({} as User);
    const { setOpenMessage, setMessage } = useContext(MessageContext);
    const { setIsLoadingContent } = useContext(LoadingContext);
    const navigate = useNavigate();

    // Get the URL search params
    const searchParams = new URLSearchParams(window.location.hash.split('?')[1]);
    const emailVerificationId = searchParams.get('id');
    const emailVerificationHash = searchParams
        .get('hash')
        ?.concat('?', window.location.hash.split('?')[2]);
    const emailVerificationSend = searchParams.get('send') == 'true' ? true : false;

    const [openRegisterBox, setOpenRegisterBox] = useState<boolean>(true);
    const [isSentEmail, setIsSentEmail] = useState<boolean>(false);

    const handleBackPress = async () => {
        const result = await logout(userAuth.token, navigate);

        if (result && typeof result === 'object' && Object.keys(result).includes('error')) {
            const error = result as Error;

            setMessage({
                type: 'error',
                content: error.message
            });
            setOpenMessage(true);
        }
    };

    const handleSendEmailVerification = async () => {
        setIsLoadingContent(true);

        const result = await sendEmailVerification(userAuth.token);

        if (result && typeof result === 'object' && !Object.keys(result).includes('error')) {
            if (result.status === 'SENT') {
                setMessage({
                    type: 'success',
                    content: 'E-mail de verificação encaminhado com sucesso'
                });
                setOpenMessage(true);

                setIsSentEmail(true);
                setIsLoadingContent(false);
            } else {
                setMessage({
                    type: 'info',
                    content: 'Este e-mail já foi verificado'
                });
                setOpenMessage(true);

                setTimeout(() => {
                    setIsLoadingContent(false);
                    navigate('/');
                }, 1000);
            }
        } else {
            const error = result as Error;

            setMessage({
                type: 'error',
                content: error.message
            });
            setOpenMessage(true);

            setIsLoadingContent(false);
        }
    };

    const handleVerifyEmail = async () => {
        setIsLoadingContent(true);

        const result = await verifyEmail(userAuth.token, {
            id: emailVerificationId ?? '',
            hash: emailVerificationHash ?? ''
        });

        if (result && typeof result === 'object' && !Object.keys(result).includes('error')) {
            if (result.status === 'VERIFIED') {
                setMessage({
                    type: 'success',
                    content: 'E-mail verificado com sucesso'
                });
                setOpenMessage(true);

                // Save session
                saveSessionCredentials(result.user as User);
            } else {
                setMessage({
                    type: 'info',
                    content: 'Este e-mail já foi verificado'
                });
                setOpenMessage(true);
            }

            setTimeout(() => {
                setIsLoadingContent(false);
                navigate('/');
            }, 1000);
        } else {
            const error = result as Error;

            setMessage({
                type: 'error',
                content: error.message
            });
            setOpenMessage(true);

            setIsLoadingContent(false);
        }
    };

    useEffect(() => {
        const user = verifyRoutePermissions(['auth', 'not.email.verified'], navigate);

        if (typeof user === 'object') {
            setUserAuth(user);
        }
    }, []);

    useEffect(() => {
        if (userAuth.token) {
            if (emailVerificationId && emailVerificationHash) {
                handleVerifyEmail();
            } else if (emailVerificationSend) {
                handleSendEmailVerification();
            }
        }
    }, [userAuth]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.mainLeft}></div>

            <div className={styles.mainRight}>
                <img src={TrilogicIconLogin} alt='Logo do Trilogic' />
            </div>

            <LoginBox open={openRegisterBox}>
                {isSentEmail ? (
                    <div className={`${styles.loginOption} ${styles.credentials}`}>
                        <div className={`${styles.optionHeader} ${styles.centralize}`}>
                            Verificação de E-mail
                        </div>

                        <p>
                            Um e-mail de verificação foi enviado para{' '}
                            <a href={`mailto:${userAuth.email}`} target='_blank'>
                                {userAuth.email}
                            </a>
                            . Por favor, verifique sua caixa de e-mail e clique no link de
                            verificação para ativar sua conta.
                        </p>

                        <button
                            type='button'
                            className={`${styles.loginOption} ${styles.redButton}`}
                            onClick={handleBackPress}
                        >
                            Sair da conta
                        </button>
                    </div>
                ) : (
                    <div className={`${styles.loginOption} ${styles.credentials}`}>
                        <div className={`${styles.optionHeader} ${styles.centralize}`}>
                            Verificação de E-mail
                        </div>

                        <form>
                            <div className={styles.groupField}>
                                <label htmlFor='email'>E-mail</label>
                                <input
                                    type='email'
                                    id='email'
                                    name='email'
                                    value={userAuth.email ?? ''}
                                    disabled={true}
                                />
                            </div>

                            <button
                                type='button'
                                className={`${styles.loginOption} ${styles.submitButton}`}
                                onClick={handleSendEmailVerification}
                            >
                                Enviar link de verificação
                            </button>

                            <button
                                type='button'
                                className={`${styles.loginOption} ${styles.redButton}`}
                                onClick={handleBackPress}
                            >
                                Sair da conta
                            </button>
                        </form>
                    </div>
                )}
            </LoginBox>
        </div>
    );
};
